import React, { useEffect, useMemo, useRef, useState } from "react";
import Multiselect from "multiselect-react-dropdown";
import { showToast } from "../Constent/Toast";

const API_BASE = "https://api.placestovisitindia.com";

const sectionCard = {
  background: "#fff", borderRadius: 10, padding: 22, marginBottom: 24,
  border: "1px solid #e2e5ed", boxShadow: "0 2px 6px rgba(0,0,0,.03)",
};
const sectionTitle = {
  fontSize: 18, fontWeight: 700, color: "#008c93",
  marginBottom: 6, paddingBottom: 8, borderBottom: "1px solid #e2e5ed",
};
const subtitle = { fontSize: 13, color: "#6c757d", marginBottom: 14 };
const primaryBtn = {
  background: "#008c93", color: "#fff", border: "none",
  padding: "10px 24px", borderRadius: 6, fontSize: 14, cursor: "pointer",
};
const ghostBtn = {
  background: "#f2f4f8", color: "#324058", border: "1px solid #d7dbe6",
  padding: "6px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer",
};
const dangerBtn = {
  background: "#fff", color: "#b3261e", border: "1px solid #f2c6c1",
  padding: "6px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer",
};
const inputStyle = {
  width: "100%", padding: "8px 10px", border: "1px solid #d7dbe6",
  borderRadius: 6, fontSize: 14, outline: "none",
};

const FixedDepartures = () => {
  const [allCats, setAllCats] = useState([]);
  const [featuredIds, setFeaturedIds] = useState([]); // ordered
  const [gridIds, setGridIds] = useState([]);         // ordered
  const [testimonials, setTestimonials] = useState([]);
  const [heroVideoUrl, setHeroVideoUrl] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [removeVideo, setRemoveVideo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const videoInputRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/admin/fixed-departures`, {
      headers: {
        "Accept": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("Token")}`,
      },
    })
      .then((r) => r.json())
      .then((d) => {
        setAllCats(d.all_categories || []);
        setFeaturedIds((d.featured || []).map((r) => r.package_category_id));
        setGridIds((d.grid || []).map((r) => r.package_category_id));
        setTestimonials(d.testimonials || []);
        setHeroVideoUrl(d?.settings?.hero_video ? `${API_BASE}/storage/${d.settings.hero_video}` : null);
      })
      .catch((e) => showToast("error", "Failed to load: " + e.message))
      .finally(() => setLoading(false));
  }, []);

  const catsById = useMemo(() => {
    const m = {};
    allCats.forEach((c) => (m[c.id] = c));
    return m;
  }, [allCats]);

  const selectedObjs = (ids) => ids.map((id) => catsById[id]).filter(Boolean);

  const move = (arr, i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const next = [...arr];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      if (videoFile) fd.append("video", videoFile);
      if (removeVideo) fd.append("remove_video", "1");
      fd.append("featured_ids", JSON.stringify(featuredIds));
      fd.append("grid_ids", JSON.stringify(gridIds));
      fd.append(
        "testimonials",
        JSON.stringify(testimonials.map((t) => ({
          name: t.name || "", rating: Number(t.rating) || 5, description: t.description || "",
        })))
      );
      const res = await fetch(`${API_BASE}/api/admin/fixed-departures`, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("Token")}`,
        },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Save failed");
      showToast("success", "Fixed Departures page saved.");
      setVideoFile(null);
      setRemoveVideo(false);
      if (data.hero_video) setHeroVideoUrl(data.hero_video);
      else if (removeVideo) setHeroVideoUrl(null);
      if (videoInputRef.current) videoInputRef.current.value = "";
    } catch (e) {
      showToast("error", e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40 }}>Loading…</div>;

  return (
    <div style={{ padding: 30, background: "#f7f9fc", minHeight: "100vh" }}>
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <h1 style={{ fontSize: 24, marginBottom: 6, color: "#1a2340" }}>Fixed Departures — Page Settings</h1>
        <p style={{ color: "#6c757d", marginBottom: 24 }}>Controls the /fixed-departures landing page on the site.</p>

        {/* ==================== HERO VIDEO ==================== */}
        <div style={sectionCard}>
          <div style={sectionTitle}>1. Hero background video</div>
          <div style={subtitle}>Uploads replace the current video. MP4/WebM/MOV, up to 50 MB.</div>
          {heroVideoUrl && !removeVideo && (
            <div style={{ marginBottom: 12 }}>
              <video src={heroVideoUrl} controls style={{ width: "100%", maxWidth: 480, borderRadius: 8, background: "#000" }} />
              <div style={{ marginTop: 8 }}>
                <button type="button" style={dangerBtn} onClick={() => setRemoveVideo(true)}>Remove current video</button>
              </div>
            </div>
          )}
          {removeVideo && (
            <div style={{ marginBottom: 12, color: "#b3261e", fontSize: 13 }}>
              Marked for removal — will be deleted on save.{" "}
              <button type="button" style={ghostBtn} onClick={() => setRemoveVideo(false)}>Undo</button>
            </div>
          )}
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
          />
          {videoFile && <div style={{ fontSize: 13, color: "#324058", marginTop: 6 }}>Pending upload: <b>{videoFile.name}</b></div>}
        </div>

        {/* ==================== FEATURED TOURS ==================== */}
        <div style={sectionCard}>
          <div style={sectionTitle}>2. Featured tours (filter row on the page)</div>
          <div style={subtitle}>Pick package categories to feature. Cards on the page are grouped and filterable by these.</div>
          <div style={{ marginBottom: 12 }}>
            <Multiselect
              options={allCats}
              displayValue="name"
              selectedValues={selectedObjs(featuredIds)}
              onSelect={(list) => setFeaturedIds(list.map((c) => c.id))}
              onRemove={(list) => setFeaturedIds(list.map((c) => c.id))}
              placeholder="-- pick package categories --"
            />
          </div>
          <OrderedList
            ids={featuredIds}
            catsById={catsById}
            onUp={(i) => setFeaturedIds(move(featuredIds, i, -1))}
            onDown={(i) => setFeaturedIds(move(featuredIds, i, 1))}
            onRemove={(i) => setFeaturedIds(featuredIds.filter((_, x) => x !== i))}
          />
        </div>

        {/* ==================== GRID CATEGORIES ==================== */}
        <div style={sectionCard}>
          <div style={sectionTitle}>3. &quot;Find tours by category&quot; grid</div>
          <div style={subtitle}>Choose the 6 categories shown as tiles below the featured tours.</div>
          <div style={{ marginBottom: 12 }}>
            <Multiselect
              options={allCats}
              displayValue="name"
              selectedValues={selectedObjs(gridIds)}
              onSelect={(list) => setGridIds(list.map((c) => c.id))}
              onRemove={(list) => setGridIds(list.map((c) => c.id))}
              placeholder="-- pick package categories --"
            />
          </div>
          <OrderedList
            ids={gridIds}
            catsById={catsById}
            onUp={(i) => setGridIds(move(gridIds, i, -1))}
            onDown={(i) => setGridIds(move(gridIds, i, 1))}
            onRemove={(i) => setGridIds(gridIds.filter((_, x) => x !== i))}
          />
        </div>

        {/* ==================== TESTIMONIALS ==================== */}
        <div style={sectionCard}>
          <div style={sectionTitle}>4. Testimonials</div>
          <div style={subtitle}>Reviewer name, star rating (1–5), and the review text.</div>
          {testimonials.map((t, i) => (
            <div key={i} style={{ border: "1px solid #e2e5ed", borderRadius: 8, padding: 12, marginBottom: 10, background: "#fafbff" }}>
              <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
                <input
                  style={{ ...inputStyle, flex: 2 }}
                  placeholder="Reviewer name"
                  value={t.name || ""}
                  onChange={(e) => setTestimonials(testimonials.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))}
                />
                <select
                  style={{ ...inputStyle, width: 120 }}
                  value={t.rating || 5}
                  onChange={(e) => setTestimonials(testimonials.map((x, xi) => xi === i ? { ...x, rating: Number(e.target.value) } : x))}
                >
                  {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{"★".repeat(n)} ({n})</option>)}
                </select>
              </div>
              <textarea
                style={{ ...inputStyle, minHeight: 70 }}
                placeholder="Review text"
                value={t.description || ""}
                onChange={(e) => setTestimonials(testimonials.map((x, xi) => xi === i ? { ...x, description: e.target.value } : x))}
              />
              <div style={{ marginTop: 8, textAlign: "right" }}>
                <button type="button" style={dangerBtn} onClick={() => setTestimonials(testimonials.filter((_, x) => x !== i))}>Remove</button>
              </div>
            </div>
          ))}
          <button type="button" style={ghostBtn} onClick={() => setTestimonials([...testimonials, { name: "", rating: 5, description: "" }])}>
            + Add testimonial
          </button>
        </div>

        {/* ==================== SAVE ==================== */}
        <div style={{ textAlign: "right" }}>
          <button type="button" style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={onSave}>
            {saving ? "Saving…" : "Save all changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* -------- helpers -------- */
const OrderedList = ({ ids, catsById, onUp, onDown, onRemove }) => {
  if (!ids.length) return <div style={{ fontSize: 13, color: "#6c757d" }}>No items yet.</div>;
  return (
    <ol style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
      {ids.map((id, i) => {
        const c = catsById[id];
        return (
          <li key={id} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 12px", border: "1px solid #e2e5ed", borderRadius: 6, marginBottom: 6, background: "#fff",
          }}>
            <span style={{ fontSize: 14 }}>
              <span style={{ color: "#6c757d", marginRight: 8 }}>{i + 1}.</span>
              {c ? c.name : <em style={{ color: "#b3261e" }}>Missing category ({id})</em>}
            </span>
            <span style={{ display: "flex", gap: 6 }}>
              <button type="button" style={ghostBtn} disabled={i === 0} onClick={() => onUp(i)}>↑</button>
              <button type="button" style={ghostBtn} disabled={i === ids.length - 1} onClick={() => onDown(i)}>↓</button>
              <button type="button" style={dangerBtn} onClick={() => onRemove(i)}>Remove</button>
            </span>
          </li>
        );
      })}
    </ol>
  );
};

export default FixedDepartures;
